import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/apiV2';
import { AuthSession } from '../types';
import { MentionInput } from './mention-input';
import { MentionText } from './mentions';

// ─────────────────────────────────────────────────────────────────────────────
// Página do Livro — Resenha + Conversa (comentários sociais).
// Reusado nos dois estados (dono / visitante). Tema escuro warm já vem das CSS
// vars do app (--paper #161210, --ember #E87A3B, --ink #F3ECDE…), idêntico ao
// design. Aqui uso rgba(232,122,59,a) para os tons ember sutis.
// ─────────────────────────────────────────────────────────────────────────────

const EMBER = '232,122,59';

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'agora';
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
  try {
    return new Date(t).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

function hueFrom(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

const Avatar: React.FC<{ name?: string | null; url?: string | null; id?: string | null; size?: number; onClick?: () => void }> = ({ name, url, id, size = 34, onClick }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const hue = hueFrom(id || name || '?');
  const base: React.CSSProperties = {
    flex: 'none', width: size, height: size, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.4, fontWeight: 600, color: '#fff', overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    background: `linear-gradient(135deg, hsl(${hue},42%,42%), hsl(${hue},48%,26%))`,
  };
  return (
    <div onClick={onClick} style={base}>
      {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}
    </div>
  );
};

// ── Estrelas ──────────────────────────────────────────────────────────────
const Stars: React.FC<{ value: number; size?: number; interactive?: boolean; onPick?: (n: number) => void }> = ({ value, size = 14, interactive, onPick }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <span style={{ display: 'inline-flex', gap: 1, lineHeight: 1 }} onMouseLeave={() => interactive && setHover(0)}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          onClick={() => interactive && onPick && onPick(n)}
          onMouseEnter={() => interactive && setHover(n)}
          style={{
            fontSize: size, letterSpacing: '0.5px', cursor: interactive ? 'pointer' : 'default',
            color: n <= shown ? 'var(--ember)' : 'var(--paper-3)',
            transition: 'color 120ms',
          }}
        >★</span>
      ))}
    </span>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// RESENHA (review highlight)
// ════════════════════════════════════════════════════════════════════════════
export const BookReview: React.FC<{
  variant: 'owner' | 'visitor';
  rating: number | null;
  review: string | null;
  authorName: string;
  authorAvatarUrl?: string | null;
  authorId?: string | null;
  dateLabel?: string;
  canEdit?: boolean;
  ownerFirstName?: string;
  onSave?: (rating: number | null, text: string) => Promise<void> | void;
  onOpenProfile?: (uid: string) => void;
}> = ({ variant, rating, review, authorName, authorAvatarUrl, authorId, dateLabel, canEdit, ownerFirstName, onSave, onOpenProfile }) => {
  const [editing, setEditing] = useState(false);
  const [draftRating, setDraftRating] = useState(rating || 0);
  const [draftText, setDraftText] = useState(review || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraftRating(rating || 0); setDraftText(review || ''); }, [rating, review, editing]);

  const hasReview = !!(review && review.trim()) || !!rating;
  const isOwner = variant === 'owner';

  const doSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try { await onSave(draftRating || null, draftText.trim()); setEditing(false); }
    finally { setSaving(false); }
  };

  // ─ Editor (dono) ─
  if (isOwner && editing) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
          <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 500 }}>Sua resenha</span>
          <Stars value={draftRating} size={15} interactive onPick={setDraftRating} />
        </div>
        <MentionInput multiline value={draftText} onChange={setDraftText} autoFocus rows={4} dropdownPosition="below"
          placeholder="O que você achou deste livro? Conte sem pressa…"
          inputStyle={{
            width: '100%', resize: 'vertical', boxSizing: 'border-box',
            background: `rgba(${EMBER},0.05)`, border: `1px solid rgba(${EMBER},0.22)`,
            borderRadius: 14, padding: '14px 15px', color: 'var(--ink)',
            fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 17, lineHeight: 1.5,
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={() => setEditing(false)} disabled={saving} style={{ flex: 1, padding: 10, borderRadius: 12, background: 'var(--paper-3)', color: 'var(--ink-2)', fontSize: 13, border: 'none' }}>Cancelar</button>
          <button onClick={doSave} disabled={saving} style={{ flex: 1, padding: 10, borderRadius: 12, background: 'var(--ember)', color: '#1a120c', fontSize: 13, fontWeight: 600, border: 'none' }}>{saving ? '…' : 'Salvar resenha'}</button>
        </div>
      </div>
    );
  }

  // ─ Dono sem resenha ainda: convite a escrever ─
  if (isOwner && !hasReview) {
    return (
      <button onClick={() => setEditing(true)} style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: `rgba(${EMBER},0.05)`, border: `1px dashed rgba(${EMBER},0.30)`,
        borderRadius: 16, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 22 }}>✍️</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', color: 'var(--ink)', fontSize: 14, fontWeight: 500 }}>Escreva sua resenha</span>
          <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12.5, marginTop: 2 }}>Dê uma nota e conte o que achou — seus amigos veem aqui.</span>
        </span>
      </button>
    );
  }

  // ─ Visitante sem resenha do dono ─
  if (!isOwner && !hasReview) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, fontStyle: 'italic', fontFamily: 'Instrument Serif, serif', padding: '4px 0' }}>
        {ownerFirstName || 'Ainda'} ainda não escreveu uma resenha deste livro.
      </div>
    );
  }

  // ─ Exibição da resenha ─
  const heading = isOwner ? 'Sua resenha' : `O que ${ownerFirstName || authorName} achou`;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: isOwner ? 'var(--ink-3)' : 'var(--ember)', fontWeight: 500 }}>{heading}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {rating ? <Stars value={rating} size={isOwner ? 11 : 11} /> : null}
          {isOwner && canEdit && (
            <button onClick={() => setEditing(true)} title="Editar resenha" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--ink-3)' }}>
              <PenIcon size={12} />
            </button>
          )}
        </div>
      </div>
      <div style={{
        background: isOwner ? `rgba(${EMBER},0.05)` : `linear-gradient(180deg,rgba(${EMBER},0.10),rgba(${EMBER},0.04))`,
        border: `1px solid rgba(${EMBER},${isOwner ? 0.14 : 0.22})`,
        borderRadius: isOwner ? 16 : 18, padding: isOwner ? '18px 19px' : '21px 21px 19px', position: 'relative',
      }}>
        <span style={{ position: 'absolute', top: isOwner ? 2 : 4, left: isOwner ? 13 : 15, fontFamily: 'Instrument Serif, serif', fontSize: isOwner ? 46 : 54, color: `rgba(${EMBER},${isOwner ? 0.32 : 0.4})`, lineHeight: 1 }}>“</span>
        <p style={{ margin: isOwner ? '8px 0 0' : '10px 0 0', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: isOwner ? 18 : 20, lineHeight: 1.5, color: isOwner ? 'var(--ink-2)' : 'var(--ink)' }}><MentionText text={review} /></p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: isOwner ? 14 : 16 }}>
          <Avatar name={authorName} url={authorAvatarUrl} id={authorId} size={isOwner ? 22 : 26} onClick={authorId && onOpenProfile ? () => onOpenProfile(authorId) : undefined} />
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{authorName}{dateLabel ? ` · ${dateLabel}` : ''}</span>
        </div>
      </div>
      {!isOwner && (
        <div style={{ textAlign: 'center', marginTop: 13, fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic', fontFamily: 'Instrument Serif, serif' }}>
          Curtiu? Pergunta pra {ownerFirstName || authorName} o que achou. ↓
        </div>
      )}
    </div>
  );
};

// ── ícones inline ────────────────────────────────────────────────────────
const PenIcon: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
);
const HeartIcon: React.FC<{ size?: number; filled?: boolean }> = ({ size = 13, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'var(--ember)' : 'none'} stroke={filled ? 'var(--ember)' : 'var(--ink-3)'} strokeWidth={2}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" /></svg>
);
const SendIcon: React.FC = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#1a120c" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
);

// ════════════════════════════════════════════════════════════════════════════
// CONVERSA (comentários)
// ════════════════════════════════════════════════════════════════════════════
type CItem = {
  id: string; parentId: string | null; author: { id: string; name: string; avatarUrl: string | null } | null;
  authorId: string; text: string | null; deleted: boolean; createdAt: string; editedAt: string | null;
  likes: number; likedByMe: boolean; isMine: boolean; canModerate: boolean; canEdit: boolean;
  replies?: CItem[];
};

export const BookConversation: React.FC<{
  session: AuthSession | null;
  bookId: string;
  variant: 'owner' | 'visitor';
  canComment: boolean;
  ownerFirstName?: string;
  myName?: string;
  myAvatarUrl?: string | null;
  onOpenProfile?: (uid: string) => void;
  onBlock?: (authorId: string, authorName: string) => Promise<void> | void;
}> = ({ session, bookId, variant, canComment, ownerFirstName, myName, myAvatarUrl, onOpenProfile, onBlock }) => {
  const [comments, setComments] = useState<CItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try { const r = await api.listBookComments(session, bookId); setComments((r.comments as any) || []); }
    catch { /* silencioso */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookId]);

  const total = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  const submit = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.addBookComment(session, bookId, t, replyTo?.id || null);
      setText(''); setReplyTo(null);
      await load();
    } catch (e: any) { window.alert(e?.message || 'Não foi possível comentar.'); }
    finally { setSending(false); }
  };

  const toggleLike = async (c: CItem) => {
    // otimista
    const apply = (list: CItem[]): CItem[] => list.map(x => {
      const upd = x.id === c.id ? { ...x, likedByMe: !x.likedByMe, likes: x.likes + (x.likedByMe ? -1 : 1) } : x;
      return { ...upd, replies: x.replies ? apply(x.replies) : x.replies };
    });
    setComments(prev => apply(prev));
    try { await api.likeBookComment(session, bookId, c.id); }
    catch { setComments(prev => apply(prev)); /* desfaz */ }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const t = editing.text.trim(); if (!t) return;
    try { await api.editBookComment(session, bookId, editing.id, t); setEditing(null); await load(); }
    catch (e: any) { window.alert(e?.message || 'Erro ao editar.'); }
  };

  const remove = async (c: CItem) => {
    setMenuFor(null);
    if (!window.confirm(c.canModerate && !c.isMine ? 'Remover este comentário do seu livro?' : 'Excluir seu comentário?')) return;
    try { await api.deleteBookComment(session, bookId, c.id); await load(); }
    catch (e: any) { window.alert(e?.message || 'Erro ao excluir.'); }
  };
  const report = async (c: CItem) => {
    setMenuFor(null);
    try { await api.reportBookComment(session, bookId, c.id, ''); window.alert('Denúncia enviada. Obrigado.'); }
    catch (e: any) { window.alert(e?.message || 'Erro ao denunciar.'); }
  };
  const block = async (c: CItem) => {
    setMenuFor(null);
    if (!c.author || !onBlock) return;
    if (!window.confirm(`Bloquear ${c.author.name}? Você não verá mais essa pessoa.`)) return;
    try { await onBlock(c.author.id, c.author.name); await load(); }
    catch (e: any) { window.alert(e?.message || 'Erro ao bloquear.'); }
  };

  const startReply = (c: CItem) => {
    setReplyTo({ id: c.id, name: c.author?.name || 'comentário' });
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const renderComment = (c: CItem, depth: number) => {
    const isEditing = editing?.id === c.id;
    const showMenu = menuFor === c.id;
    const canDoMenu = c.canEdit || c.isMine || c.canModerate || (!c.isMine && !!c.author); // editar/excluir/denunciar/bloquear
    return (
      <div key={c.id} style={{ display: 'flex', gap: depth ? 10 : 12 }}>
        <Avatar name={c.author?.name} url={c.author?.avatarUrl} id={c.author?.id} size={depth ? 28 : 34}
          onClick={c.author?.id && onOpenProfile ? () => onOpenProfile(c.author!.id) : undefined} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span onClick={c.author?.id && onOpenProfile ? () => onOpenProfile(c.author!.id) : undefined}
              style={{ fontSize: depth ? 12.5 : 13.5, fontWeight: 600, color: 'var(--ink)', cursor: c.author?.id && onOpenProfile ? 'pointer' : 'default' }}>
              {c.author?.name || 'Alguém'}
            </span>
            <span style={{ fontSize: depth ? 10.5 : 11, color: 'var(--ink-4)' }}>{timeAgo(c.createdAt)}{c.editedAt ? ' · editado' : ''}</span>
            {canDoMenu && !c.deleted && (
              <div style={{ position: 'relative', marginLeft: 'auto' }}>
                <button onClick={() => setMenuFor(showMenu ? null : c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', padding: '0 2px', fontSize: 16, lineHeight: 1 }}>⋯</button>
                {showMenu && (
                  <>
                    <div onClick={() => setMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div style={{ position: 'absolute', right: 0, top: 20, zIndex: 41, minWidth: 168, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 30px rgba(0,0,0,0.4)' }}>
                      {c.canEdit && <MenuBtn label="Editar" onClick={() => { setMenuFor(null); setEditing({ id: c.id, text: c.text || '' }); }} />}
                      {(c.isMine || c.canModerate) && <MenuBtn label={c.isMine ? 'Excluir' : 'Remover comentário'} danger onClick={() => remove(c)} />}
                      {!c.isMine && <MenuBtn label="Denunciar" onClick={() => report(c)} />}
                      {!c.isMine && c.canModerate && onBlock && <MenuBtn label="Bloquear pessoa" danger onClick={() => block(c)} />}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {c.deleted ? (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-4)', fontStyle: 'italic' }}>comentário removido</p>
          ) : isEditing ? (
            <div style={{ marginTop: 6 }}>
              <MentionInput multiline value={editing!.text} onChange={(v) => setEditing({ id: c.id, text: v })} rows={2} autoFocus dropdownPosition="below"
                inputStyle={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px', color: 'var(--ink)', fontSize: 13.5, lineHeight: 1.5 }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button onClick={() => setEditing(null)} style={{ padding: '6px 12px', borderRadius: 9, background: 'var(--paper-3)', color: 'var(--ink-2)', fontSize: 12, border: 'none' }}>Cancelar</button>
                <button onClick={saveEdit} style={{ padding: '6px 12px', borderRadius: 9, background: 'var(--ember)', color: '#1a120c', fontSize: 12, fontWeight: 600, border: 'none' }}>Salvar</button>
              </div>
            </div>
          ) : (
            <p style={{ margin: '4px 0 0', fontSize: depth ? 13 : 13.5, lineHeight: 1.5, color: 'var(--ink-2)' }}><MentionText text={c.text} /></p>
          )}

          {!c.deleted && !isEditing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
              <button onClick={() => toggleLike(c)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: c.likedByMe ? 'var(--ember)' : 'var(--ink-3)' }}>
                <HeartIcon size={13} filled={c.likedByMe} />{c.likes > 0 ? c.likes : ''}
              </button>
              {depth === 0 && canComment && (
                <button onClick={() => startReply(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Responder</button>
              )}
            </div>
          )}

          {/* respostas */}
          {c.replies && c.replies.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
              {c.replies.map(r => renderComment(r, depth + 1))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 500 }}>Conversa</span>
        {total > 0 && <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>· {total}</span>}
      </div>

      {/* Composer */}
      {canComment ? (
        variant === 'visitor' ? (
          <div style={{ background: `rgba(${EMBER},0.06)`, border: `1px solid rgba(${EMBER},0.18)`, borderRadius: 18, padding: '15px 16px', marginBottom: 20 }}>
            <div style={{ fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 17, color: 'var(--ink)', marginBottom: 11 }}>
              {replyTo ? `Respondendo ${replyTo.name}` : `Puxe assunto com ${ownerFirstName || 'a pessoa'} sobre este livro`}
            </div>
            <Composer {...{ text, setText, submit, sending, inputRef, replyTo, setReplyTo, myName, myAvatarUrl, placeholder: 'O que você achou deste livro?' }} />
          </div>
        ) : (
          <div style={{ marginBottom: 18 }}>
            {replyTo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--ink-3)' }}>
                Respondendo <strong style={{ color: 'var(--ink-2)' }}>{replyTo.name}</strong>
                <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer' }}>✕</button>
              </div>
            )}
            <Composer {...{ text, setText, submit, sending, inputRef, replyTo, setReplyTo, myName, myAvatarUrl, placeholder: 'Responda ou puxe assunto…' }} />
          </div>
        )
      ) : (
        <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 16px', marginBottom: 20, fontSize: 13, color: 'var(--ink-3)' }}>
          Este perfil é privado. Siga para poder comentar.
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div style={{ color: 'var(--ink-4)', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>Carregando conversa…</div>
      ) : total === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13.5, textAlign: 'center', padding: '14px 0', fontStyle: 'italic', fontFamily: 'Instrument Serif, serif' }}>
          Ainda ninguém comentou. Seja o primeiro a puxar assunto.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {comments.map(c => renderComment(c, 0))}
        </div>
      )}
    </div>
  );
};

const MenuBtn: React.FC<{ label: string; danger?: boolean; onClick: () => void }> = ({ label, danger, onClick }) => (
  <button onClick={onClick} style={{ width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', borderBottom: '1px solid var(--line)', cursor: 'pointer', fontSize: 13, color: danger ? 'var(--ember)' : 'var(--ink-2)' }}>{label}</button>
);

const Composer: React.FC<any> = ({ text, setText, submit, sending, inputRef, myName, myAvatarUrl, placeholder }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
    <Avatar name={myName} url={myAvatarUrl} size={32} />
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 99, padding: '6px 6px 6px 15px' }}>
      <MentionInput
        inputRef={inputRef}
        value={text}
        onChange={setText}
        onEnter={submit}
        placeholder={placeholder}
        dropdownPosition="above"
        wrapStyle={{ flex: 1, minWidth: 0 }}
        inputStyle={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: 'var(--ink)', fontSize: 13 }}
      />
      <button onClick={submit} disabled={sending || !text.trim()} style={{ flex: 'none', width: 30, height: 30, borderRadius: '50%', background: text.trim() ? 'var(--ember)' : 'var(--paper-3)', border: 'none', cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: sending ? 0.6 : 1 }}>
        <SendIcon />
      </button>
    </div>
  </div>
);
